import React, { useState, useCallback, createContext, useContext, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle, Trash2, LogOut, X, Info, CheckCircle2, ShieldAlert } from 'lucide-react';

const ConfirmContext = createContext();

const typeConfig = {
  danger:   { icon: <Trash2 size={18} className="text-red-600" />,     iconBg: 'bg-red-50 dark:bg-red-950/40',    btn: 'bg-red-600 hover:bg-red-700' },
  warning:  { icon: <AlertTriangle size={18} className="text-amber-600" />, iconBg: 'bg-amber-50 dark:bg-amber-950/40', btn: 'bg-amber-600 hover:bg-amber-700' },
  logout:   { icon: <LogOut size={18} className="text-red-600" />,      iconBg: 'bg-red-50 dark:bg-red-950/40',    btn: 'bg-red-600 hover:bg-red-700' },
  info:     { icon: <Info size={18} className="text-navy-700" />,       iconBg: 'bg-navy-50 dark:bg-navy-950/40',  btn: 'bg-navy-700 hover:bg-navy-800' },
  success:  { icon: <CheckCircle2 size={18} className="text-green-700" />, iconBg: 'bg-green-50 dark:bg-green-950/40', btn: 'bg-green-700 hover:bg-green-800' },
  critical: { icon: <ShieldAlert size={18} className="text-red-700" />, iconBg: 'bg-red-100 dark:bg-red-950/50',   btn: 'bg-red-700 hover:bg-red-800' },
};

function ConfirmModal({ title, message, confirmText, cancelText, type, onConfirm, onCancel }) {
  const config = typeConfig[type] || typeConfig.warning;

  return (
    <motion.div
      key="confirm-backdrop"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.15 }}
      className="fixed inset-0 z-[10000] bg-black/60 backdrop-blur-xs flex items-center justify-center p-4"
      onClick={onCancel}
    >
      <motion.div
        key="confirm-card"
        initial={{ scale: 0.95, opacity: 0, y: 14 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.95, opacity: 0, y: 14 }}
        transition={{ type: 'spring', damping: 25, stiffness: 350 }}
        className="bg-white dark:bg-[#161514] border border-[#e2e0db] dark:border-[#2e2d2b] rounded-lg p-5 w-full max-w-sm shadow-2xl relative"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-start gap-3 mb-4">
          <div className={`w-9 h-9 ${config.iconBg} rounded-md flex items-center justify-center shrink-0`}>
            {config.icon}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-sm text-[#1a1917] dark:text-white">{title}</h3>
            <p className="text-xs text-[#6b6860] dark:text-[#9c9890] mt-1 leading-relaxed">{message}</p>
          </div>
          <button
            onClick={onCancel}
            className="w-6 h-6 rounded bg-[#f8f7f4] dark:bg-[#222] flex items-center justify-center text-[#6b6860] hover:text-[#1a1917] shrink-0 transition-colors"
          >
            <X size={12} />
          </button>
        </div>

        <div className="flex gap-2">
          <button
            onClick={onCancel}
            className="flex-1 py-2 rounded-md font-semibold text-xs text-[#6b6860] dark:text-[#9c9890] bg-[#f8f7f4] dark:bg-[#222] hover:bg-[#e8eef5] dark:hover:bg-[#333] transition-colors"
          >
            {cancelText || 'Cancel'}
          </button>
          <button
            onClick={onConfirm}
            className={`flex-1 py-2 rounded-md font-semibold text-xs text-white ${config.btn} transition-colors shadow-sm`}
          >
            {confirmText || 'Confirm'}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

export function ConfirmProvider({ children }) {
  const [state, setState] = useState({ isOpen: false, title: '', message: '', confirmText: '', cancelText: '', type: 'warning', resolve: null });

  // Body scroll lock while confirm modal is active
  useEffect(() => {
    if (state.isOpen) {
      const orig = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = orig;
      };
    }
  }, [state.isOpen]);

  const confirm = useCallback(({ title, message, confirmText, cancelText, type }) => {
    return new Promise(resolve => {
      setState({ isOpen: true, title, message, confirmText, cancelText, type: type || 'warning', resolve });
    });
  }, []);

  const handleConfirm = () => { state.resolve?.(true); setState(s => ({ ...s, isOpen: false })); };
  const handleCancel = () => { state.resolve?.(false); setState(s => ({ ...s, isOpen: false })); };

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {typeof document !== 'undefined' && createPortal(
        <AnimatePresence>
          {state.isOpen && (
            <ConfirmModal
              title={state.title}
              message={state.message}
              confirmText={state.confirmText}
              cancelText={state.cancelText}
              type={state.type}
              onConfirm={handleConfirm}
              onCancel={handleCancel}
            />
          )}
        </AnimatePresence>,
        document.body
      )}
    </ConfirmContext.Provider>
  );
}

export function useConfirm() { return useContext(ConfirmContext); }
